import { memo, useState, type FC } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import { Markdown } from "../../Markdown";
import NextImage from "../../NextImage";
import { ReasoningList, RelevantModal } from "./RelevantModal";
import Translations from "@translate/components/Chat/DeepSearch/DeepSearchContent";
import { I18nUtil } from "@utils/i18n";
import type { IToolCallList, useChats } from "@/src/hooks/useChats";
import { useIsPc } from "@hooks/useIsPc";

const I18n = I18nUtil.createScoped(Translations);

type MessagePropsType = {
  data: {
    toolCallList: IToolCallList[];
    stepList: ReturnType<typeof useChats>["messages"][0]["stepList"];
  };
};

const DeepSearchContent: FC<MessagePropsType> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentResults, setCurrentResult] = useState<ReasoningList[]>([]);
  const [isPC] = useIsPc();
  const { toolCallList, stepList = [] } = data;
  return (
    <div>
      {toolCallList.map((toolCall, index: number) => {
        const { header, summary, args, result, state, step, toolName, id } =
          toolCall;
        const isSearching = state === "partial-call" || state === "call";
        const isFinish = stepList?.find(
          (item: any) => item.status === "finish",
        );

        return (
          <>
            {header && (
              <div
                key={`${header}_${index}`}
                data-id={header}
                className="flex data-sticky-header items-center gap-1 left-0 w-full z-10"
              >
                {!isPC && !isFinish && (
                  <div className="mt-4">
                    <Icon
                      name="ic_loading"
                      className="animate-spin"
                      size={14}
                      color={ospThemes.colors.content_tertiary}
                    />
                  </div>
                )}
                <div
                  id={`deep-search-${step || id}`}
                  className="mb-2 body_medium_l pt-6 text-content_primary"
                >
                  {header}
                </div>
              </div>
            )}
            <div key={index}>
              {summary && <Markdown>{summary}</Markdown>}
              {toolName && (
                <div className="my-3">
                  <div className="flex gap-1 items-start">
                    {/* partial-call */}
                    <Icon
                      name={isSearching ? "Spinner" : "search"}
                      className={cn("text-content_secondary mt-[2px]", {
                        "animate-spin": isSearching,
                      })}
                      size={16}
                    />
                    {/* "call" */}
                    {args?.question && (
                      <div className="body_m text-content_secondary">
                        {I18n.t("Searching for")}
                        <span className="text-content_primary ml-1">
                          {`"${args.question}"`}
                        </span>
                      </div>
                    )}
                  </div>
                  {/*  "result" */}
                  {result?.urls?.length && (
                    <div className="flex flex-col gap-2 mt-2">
                      {result.urls.slice(0, 3).map((item, index: number) => {
                        const { url, title, favicon, domain } = item;
                        return (
                          <ShellLink
                            aria-label="Deep Search Content"
                            data-openreplay-label="chatbox_deepsearchcontent_open"
                            id="lnk-deep-search-content"
                            href={url}
                            key={index}
                            target="_blank"
                            className="flex flex-col gap-2 md:flex-row"
                          >
                            <div className="flex gap-2">
                              {favicon && (
                                <NextImage
                                  src={favicon}
                                  alt={title}
                                  placeholderSrc="/assets/images/globe_web_icon.png"
                                  className="w-4 h-4 mt-[2px] min-w-4"
                                />
                              )}
                              <div className="body_m max-w-[300px] text-content_secondary md:max-w-[330px] truncate">
                                {title}
                              </div>
                            </div>
                            <div className="text-content_quaternary body_m">
                              {domain}
                            </div>
                          </ShellLink>
                        );
                      })}
                      {result.urls.length > 3 && (
                        <div
                          data-openreplay-label="chatbox_deepsearchcontent_click"
                          className="text-content_tertiary body_s cursor-pointer"
                          onClick={() => {
                            setCurrentResult(result.urls || []);
                            setIsOpen(true);
                          }}
                        >
                          {I18n.t("see %{var0} more", {
                            var0: result.urls.length - 3,
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        );
      })}
      <RelevantModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        urls={currentResults}
      />
    </div>
  );
};

export default memo(DeepSearchContent);
